import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import type { Flagship } from "@/lib/brand";
import { PhoneMockup } from "@/components/public/mockups/PhoneMockup";

interface FlagshipCardProps {
    flagship: Flagship;
}

export const FlagshipCard = ({ flagship }: FlagshipCardProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const prefersReducedMotion = useReducedMotion();

    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const mouseXSpring = useSpring(x, { stiffness: 120, damping: 15 });
    const mouseYSpring = useSpring(y, { stiffness: 120, damping: 15 });

    const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["7deg", "-7deg"]);
    const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-7deg", "7deg"]);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!ref.current || prefersReducedMotion) return;
        const rect = ref.current.getBoundingClientRect();
        x.set((e.clientX - rect.left) / rect.width - 0.5);
        y.set((e.clientY - rect.top) / rect.height - 0.5);
    };

    const handleMouseLeave = () => {
        x.set(0);
        y.set(0);
    };

    // Map flagship key to screenshot
    const imageSrc = `/mockups/${flagship.key}-phone.png`;

    return (
        <div
            ref={ref}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            className="group relative pt-16 [perspective:1400px]"
        >
            <motion.a
                href={flagship.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                    rotateX: prefersReducedMotion ? 0 : rotateX,
                    rotateY: prefersReducedMotion ? 0 : rotateY,
                    transformStyle: "preserve-3d",
                }}
                className={`relative block bento-card p-8 bg-gradient-to-br ${flagship.gradient} border border-white/10 hover:border-white/20 transition-colors duration-300 hover:shadow-2xl hover:shadow-primary/10 min-h-[300px]`}
            >
                {/* External link icon — visual-right (start in RTL) */}
                <div
                    className="absolute top-6 right-6 opacity-40 group-hover:opacity-100 transition-opacity z-10"
                    style={{ transform: "translateZ(30px)" }}
                >
                    <ExternalLink className="w-4 h-4 text-white" />
                </div>

                {/* Phone mockup — peeks out of visual-left edge and above top */}
                <div
                    className="pointer-events-none absolute -top-[70px] -left-3 z-20"
                    style={{ transformStyle: "preserve-3d" }}
                >
                    <div
                        className="animate-float"
                        style={{
                            transform: "translateZ(90px) rotateY(12deg) rotateX(4deg)",
                            transformStyle: "preserve-3d",
                            filter: "drop-shadow(0 30px 30px rgba(0,0,0,0.6))",
                        }}
                    >
                        <PhoneMockup size="sm" imageSrc={imageSrc} />
                    </div>
                </div>

                {/* Content — offset from phone on visual-left side */}
                <div
                    className="flex flex-col min-h-[250px] pl-[170px] pt-2"
                    style={{ transform: "translateZ(20px)", transformStyle: "preserve-3d" }}
                >
                    <h3 className="text-2xl md:text-3xl font-black text-white mb-1 tracking-tight">{flagship.name}</h3>
                    <p className="text-xs font-bold text-white/50 mb-4 uppercase tracking-widest">{flagship.nameAr}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed font-medium flex-1">
                        {flagship.descAr}
                    </p>
                    <div className="mt-6 flex items-center gap-2 text-xs font-bold text-primary/80 group-hover:text-primary transition-colors">
                        زيارة الموقع
                        <span className="group-hover:translate-x-1 transition-transform">←</span>
                    </div>
                </div>
            </motion.a>
        </div>
    );
};

export default FlagshipCard;
